import prefill from './registration/prefill'
import extractFormData from './registration/extractFormData'
import getCompanionData from './registration/getCompanionData'
import getBookableGroupData from './registration/getBookableGroupData'
import Choices from 'choices.js'
import debounce from '../utils/debounce'

window.registration = function (data = null) {
  return {
    errors: {},
    items: [],
    data,
    companionItems: [],
    recommendationItems: [],
    submitting: false,
    multiSelects: {},
    type: 'single_step',
    prefill,
    extractFormData,
    getCompanionData,
    getBookableGroupData,
    init() {
      this.syncSettings()
      this.initCustomFields()
      this.prefill(this.data)
      this.initializeListeners()
    },
    syncSettings() {
      this.items = this.$wire.get('items')
      this.companionItems = this.$wire.get('companionItems')
      this.recommendationItems = this.$wire.get('recommendationItems')
      this.type = this.$wire.get('formType')
    },
    initializeListeners() {
      const form = document.getElementById('registration-form')
      form.addEventListener(
        'input',
        debounce((event) => {
          // ignore file input because we handle filed update from hidden text input
          if (event.target.type === 'file') {
            return false
          }
          // ignore input from multiselect search and captcha
          if (
            event.target.classList.contains('choices__input') ||
            event.target.classList.contains('captcha-field-input')
          ) {
            return false
          }
          event.target.removeAttribute('aria-invalid')
          event.target.removeAttribute('aria-describedby')
          this.syncValues(event.target.name)
        }),
      )
      // Listen for step changes using Livewire 3 event system
      this.$wire.on('registration:form:step-changed', ([booking]) =>{
        setTimeout(() => {
          this.syncSettings()
          this.data = booking
          this.refresh()
        }, 100)
      }
      )

      // Use Livewire 3 event system
      this.$wire.on('registration:confirm-participation', () => {
        this.refresh()
      })
      this.$wire.on('prefill', () => {
        setTimeout(() => this.refresh(), 200)
      })
      this.$wire.on('custom-fields:init', () => {
        this.refresh()
      })
      this.$wire.on('form:fields-cleared', ([event]) => {
        event.fields.forEach((fieldPath) => {
          const element = document.querySelector(`[name="${fieldPath}"]`)
          if (element) {
            if (element.type === 'checkbox') {
              element.checked = false
            } else if (element.type === 'select-multiple') {
              this.multiSelects[fieldPath]?.removeActiveItems()
            } else {
              element.value = ''
            }
          }
        })
      })
      this.$wire.on('registration:form-validation-failed', ([errors]) => {
        if (!errors) return
        this.errors = errors
        Object.keys(errors).forEach((fieldPath) => {
          const el = document.querySelector(`[name="${fieldPath}"]`)
          if (el) {
            el.setAttribute('aria-invalid', 'true')
            el.setAttribute('aria-describedby', 'error_' + fieldPath)
          }
        })
      })
    },

    refresh() {
      this.initCustomFields()
      this.prefill(this.data)
    },
    syncValues(forKey) {
      this.syncSettings()
      if (this.type === 'single_step') {
        this.$wire.onFieldUpdate(this.extractFormData(), forKey)
      } else {
        this.$wire.onMultiFormFieldUpdate(this.extractFormData(true), forKey)
      }
    },
    canSubmit() {
      return true
    },
    initCustomFields() {
      this.multiSelects = {}
      const multiSelectsElements = document.querySelectorAll('select[multiple]')
      const multiSelects = this.multiSelects
      multiSelectsElements.forEach((e) => {
        const key = e.name
        if (Object.prototype.hasOwnProperty.call(multiSelects, key)) {
          multiSelects[key].destroy()
        }
        multiSelects[key] = new Choices(e, { removeItemButton: true })
        e.addEventListener(
          'change',
          () => {
            setTimeout(() => this.syncValues(key), 100)
          },
          false,
        )
      })
      this.multiSelects = multiSelects
    },
    submit() {
      if (this.canSubmit()) {
        const data = this.extractFormData()
        if (this.captcha) {
          data.captcha = this.captcha
        }
        const hash = this.getHashParameter()
        const section = this.getRegistrationSection()
        this.$wire.submit({ ...data, hash }).then(() => {
          this.revealSubmissionResult(hash, section)
        })
      }
    },
    /**
     * The section wrapping this form. Resolved before the submit request because
     * the Alpine root is replaced together with the form once the submission
     * succeeds, while the section element itself survives the morph.
     */
    getRegistrationSection() {
      return (
        this.$root.closest('section.registration-section') ||
        this.$root.closest('section')
      )
    },
    /**
     * Keep the guest on the registration section after the form has been replaced
     * by the confirmation (AIRLST-5416). Replacing a tall form with a short message
     * shrinks the page, which makes the browser clamp the scroll offset; scrolling
     * the section into view turns that snap into a deliberate move. The scroll is
     * deferred one frame so it runs after Livewire has morphed the DOM.
     */
    revealSubmissionResult(hash, section) {
      window.requestAnimationFrame(() => {
        if (hash) {
          window.location.hash = hash
        }
        if (section && section.isConnected) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    getHashParameter() {
      let hash = ''
      const registrationSection = document.querySelector(
        'section.registration-section',
      )
      if (registrationSection.id) {
        return `#${registrationSection.id}`
      }
      if (window.location.hash) {
        return `#${window.location.hash}`
      }
      return hash
    },
    submitMultiStepForm() {
      if (this.canSubmit()) {
        const data = this.extractFormData(true)
        if (this.captcha) {
          data.captcha = this.captcha
        }
        if (data.companions.length < 1) {
          delete data.companions
        }
        if (data.recommendations.length < 1) {
          delete data.recommendations
        }
        // Corresponding to RegistrationEvent::FRONTEND_FORM_SUBMITTED
        const hash = this.getHashParameter()
        const section = this.getRegistrationSection()
        this.$wire.submitMultiStepForm({ ...data, hash }).then(() => {
          this.revealSubmissionResult(hash, section)
        })
      }
    },
    moveForward() {
      this.$wire.moveToNextStep(this.extractFormData(true))
      document.getElementById('registration-form').scrollIntoView()
    },
    moveBack() {
      this.$wire.moveToPreviousStep(this.extractFormData(true))
      document.getElementById('registration-form').scrollIntoView()
    },
  }
}
